import { z } from 'zod';
import { MoneySchema, ShortIdSchema, TimestampSchema, UidSchema } from './primitives.js';
import { PaymentMethodSchema } from './ticket.js';

/**
 * Refund of a WASH payment (gap G8).
 *
 * Refunds were happening entirely outside Kilat: a customer complained, cash
 * came back out of the till, and nothing in the system knew. That leaves the
 * day's takings overstated, the operator's own reports wrong, and no answer at
 * all to "how often do we refund, and why".
 *
 * Distinct from `commissionClawback`, which reverses an AGENT's commission when
 * a subscription invoice is refunded. That is Kilat's money owed to an agent;
 * this is the operator's money owed to their customer, and the two never meet.
 *
 * Deliberately a REQUEST, not an instruction. Two cases, told apart by
 * `paidOnline` / `customerRefundRoute`:
 *
 *   - Paid at the till (cash, or straight into the operator's own account):
 *     Kilat never held that money, so nothing here moves funds. The record
 *     exists so the refund is counted, attributable and explainable.
 *   - Paid ONLINE through Kilat (toyyibPay, Kilat as merchant of record): Kilat
 *     holds the money on the operator's behalf. Approving the refund writes a
 *     `refund` debit to the operator's walletLedger (so the next payout is
 *     smaller), and a Kilat super-admin returns the money to the customer by
 *     hand in the toyyibPay portal — there is no automated PSP refund.
 *
 * Either way `settledMethod` records how the money actually went back.
 */

export const RefundStatusSchema = z.enum([
  /** Raised, awaiting an owner or manager decision. */
  'pending',
  /** Approved; the money still has to be handed back. */
  'approved',
  /** Declined, with a reason. Terminal. */
  'rejected',
  /** Money actually returned to the customer. Terminal. */
  'settled',
]);
export type RefundStatus = z.infer<typeof RefundStatusSchema>;

/**
 * Why the refund was asked for. A free-text-only field would make the whole
 * record unanalysable, which defeats half the point of capturing it; the
 * `other` escape hatch keeps `note` meaningful for the genuinely unusual case.
 */
export const RefundReasonSchema = z.enum([
  'damage',
  'poor_result',
  'overcharged',
  'duplicate_payment',
  'service_not_provided',
  'goodwill',
  'other',
]);
export type RefundReason = z.infer<typeof RefundReasonSchema>;

/**
 * Where the refunded money physically comes from.
 *
 *   - `till`              — the customer paid the operator directly (cash, or a
 *                           QR/e-wallet into the operator's own account). The
 *                           operator hands it back; Kilat's ledgers do not move.
 *   - `manual_toyyibpay`  — the customer paid ONLINE through Kilat as merchant of
 *                           record. Kilat holds the money, so approving the refund
 *                           debits the operator's wallet, and a Kilat super-admin
 *                           returns it to the customer by hand in the toyyibPay
 *                           merchant portal. There is no automated PSP refund.
 */
export const RefundCustomerRouteSchema = z.enum(['till', 'manual_toyyibpay']);
export type RefundCustomerRoute = z.infer<typeof RefundCustomerRouteSchema>;

/**
 * The uid the toyyibPay webhook stamps on `ticket.payment.recordedBy` when it
 * settles a ticket that was paid online. The till's "Paid online" badge and the
 * cashier double-pay guard (`recordPayment`) read the same marker.
 */
export const SYSTEM_PAYMENT_RECORDER = '__system__';

/** True when the ticket's recorded payment came in online through Kilat. */
export function isTicketPaidOnline(
  payment: { readonly recordedBy?: unknown } | null | undefined,
): boolean {
  return payment?.recordedBy === SYSTEM_PAYMENT_RECORDER;
}

/**
 * Deterministic walletLedger id for the debit an online refund writes. One
 * refund request → at most one debit, however many times the decision is
 * retried or re-delivered.
 */
export function refundWalletDebitId(refundId: string): string {
  return `refund-${refundId}`;
}

export const RefundRequestSchema = z.object({
  id: ShortIdSchema.describe('Refund request id'),
  ticketId: ShortIdSchema.describe('The archived ticket in ticketsHistory this refunds'),
  outletId: z.string().min(1).describe('Outlet the wash was sold at'),
  amountSen: MoneySchema.describe(
    'Refund amount in sen. May be LESS than the ticket total — a partial refund for one bad add-on is the common real case. The server refuses more than was paid.',
  ),
  paidMethod: PaymentMethodSchema.describe(
    'What the customer originally paid with, copied at request time',
  ),
  reason: RefundReasonSchema.describe('Structured reason, so refunds stay analysable'),
  note: z.string().max(500).optional().describe('Free text; the detail behind the reason'),

  status: RefundStatusSchema.describe('pending → approved → settled, or pending → rejected'),

  requestedBy: UidSchema.describe('Staff member who raised it'),
  requestedAt: TimestampSchema.describe('When it was raised'),

  decidedBy: UidSchema.optional().describe('Owner or manager who approved or rejected'),
  decidedAt: TimestampSchema.optional().describe('When the decision was taken'),
  decisionNote: z.string().max(500).optional().describe('Why it was approved or rejected'),

  settledMethod: PaymentMethodSchema.optional().describe('How the money actually went back'),
  settledAt: TimestampSchema.optional().describe('When the customer actually got the money'),
  settlementRef: z
    .string()
    .max(120)
    .optional()
    .describe('Bank or e-wallet reference, so a disputed refund can be traced'),

  paidOnline: z
    .boolean()
    .optional()
    .describe(
      'True when the customer paid this wash ONLINE through Kilat (toyyibPay), so Kilat — not the till — holds the money. Absent on refunds raised before 2026-10-16.',
    ),
  customerRefundRoute: RefundCustomerRouteSchema.optional().describe(
    'How the money physically gets back to the customer. `manual_toyyibpay` means a Kilat super-admin must refund it by hand in the toyyibPay portal — Kilat has no refund API.',
  ),
  walletDebitEntryId: z
    .string()
    .optional()
    .describe(
      "The `refund` entry written to the operator's walletLedger when an online refund is approved. Its id is deterministic (`refundWalletDebitId`).",
    ),
  pspRef: z
    .string()
    .optional()
    .describe('toyyibPay bill code of the original online payment, for the manual portal refund'),
});
export type RefundRequest = z.infer<typeof RefundRequestSchema>;

/**
 * Permitted status moves. One table, consulted by both the server and the UI,
 * so the two cannot disagree about what is allowed — the same rule the ticket
 * state machine follows.
 */
export const REFUND_TRANSITIONS: Readonly<Record<RefundStatus, readonly RefundStatus[]>> = {
  pending: ['approved', 'rejected'],
  approved: ['settled'],
  rejected: [],
  settled: [],
};

export function canTransitionRefund(from: RefundStatus, to: RefundStatus): boolean {
  return (REFUND_TRANSITIONS[from] ?? []).includes(to);
}

/** True once the request can never change again. */
export function isRefundTerminal(status: RefundStatus): boolean {
  return (REFUND_TRANSITIONS[status] ?? []).length === 0;
}

/**
 * How much of a ticket is still refundable, given what has already been
 * refunded against it.
 *
 * Pure and total. Guards the case that actually loses money: several partial
 * refunds against one ticket quietly adding up to more than the customer ever
 * paid. Anything already rejected does not count against the remainder.
 */
export function refundableRemainderSen(
  paidSen: number,
  existing: readonly Pick<RefundRequest, 'amountSen' | 'status'>[],
): number {
  const paid = Number.isFinite(paidSen) && paidSen > 0 ? Math.trunc(paidSen) : 0;
  const committed = existing
    .filter((r) => r.status !== 'rejected')
    .reduce((sum, r) => sum + (Number.isFinite(r.amountSen) ? Math.trunc(r.amountSen) : 0), 0);
  return Math.max(0, paid - committed);
}
